import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Plus,
  Minus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { apiGet, apiPost } from "@/lib/api";

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 20;

export default function WhatsAppNumberUpgradeDialog() {
  const { isAdmin, isSuperAdmin, refreshFeatures } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [limitData, setLimitData] = useState({
    limit: 0,
    current: 0,
    excess: 1,
  });
  const [unitPrice, setUnitPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [result, setResult] = useState(null);
  const timeoutRef = useRef(null);

  const totalPrice = unitPrice ? unitPrice * quantity : null;

  const fetchUnitPrice = useCallback(async () => {
    setPriceLoading(true);
    try {
      const res = await apiGet(
        "/api/plans/account/addon-price?featureKey=channels.whatsapp_numbers",
      );
      if (!res.ok) return;
      const data = await res.json();
      const resolved = data.priceDetails?.[0]?.price ?? data.totalPrice ?? null;
      setUnitPrice(typeof resolved === "number" ? resolved : null);
    } catch {
      setUnitPrice(null);
    } finally {
      setPriceLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleUpgradeRequired = (event) => {
      if (!isAdmin?.() && !isSuperAdmin?.()) return;

      const detail = event?.detail || {};
      const limit = Number(detail.limit) || 0;
      const current = Number(detail.current) || limit;
      const suggestedExcess = Math.max(
        MIN_QUANTITY,
        Number(detail.excess) || current - limit + 1 || 1,
      );

      setLimitData({
        limit,
        current,
        excess: suggestedExcess,
      });
      setQuantity(Math.min(MAX_QUANTITY, suggestedExcess));
      setResult(null);
      setIsOpen(true);
      fetchUnitPrice();
    };

    globalThis.addEventListener(
      "whatsapp:upgrade_required",
      handleUpgradeRequired,
    );
    return () => {
      globalThis.removeEventListener(
        "whatsapp:upgrade_required",
        handleUpgradeRequired,
      );
    };
  }, [fetchUnitPrice, isAdmin, isSuperAdmin]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const changeQuantity = (next) => {
    setQuantity(Math.max(MIN_QUANTITY, Math.min(MAX_QUANTITY, next)));
  };

  const handlePurchase = async () => {
    setPurchasing(true);
    setResult(null);
    try {
      const res = await apiPost("/api/plans/account/whatsapp-numbers/add", {
        quantity,
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setResult({
          success: false,
          message:
            data.message || data.error || "Falha ao adicionar números WhatsApp",
        });
        return;
      }

      setResult({
        success: true,
        message: `${quantity} número(s) adicionado(s)! Novo limite: ${data.newLimit}.`,
      });

      if (refreshFeatures) {
        await refreshFeatures();
      }

      globalThis.dispatchEvent(
        new CustomEvent("whatsapp:purchased", {
          detail: { newLimit: data.newLimit, quantity },
        }),
      );

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
        setResult(null);
      }, 1200);
    } catch (error) {
      const message =
        error?.status === 402
          ? "Conta com pendência financeira. Regularize o pagamento para comprar números."
          : error?.data?.error || error?.message || "Erro de conexão";
      setResult({ success: false, message });
    } finally {
      setPurchasing(false);
    }
  };

  if (!isAdmin?.() && !isSuperAdmin?.()) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="bg-surface border-border text-foreground-strong sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Limite de números WhatsApp atingido
          </DialogTitle>
          <DialogDescription className="text-foreground-muted">
            Sua conta está usando {limitData.current} de {limitData.limit}{" "}
            números WhatsApp. Compre números adicionais para continuar a
            ativação agora.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-border bg-background/50 p-3">
            <p className="text-xs text-foreground-muted">Preço unitário</p>
            <p className="text-sm font-medium text-foreground-strong">
              {priceLoading
                ? "Carregando..."
                : unitPrice
                  ? `R$ ${unitPrice.toFixed(2)} / mês`
                  : "Preço indisponível"}
            </p>
            {!priceLoading && !unitPrice && (
              <div className="mt-2 flex items-center justify-between gap-3">
                <span className="text-xs text-amber-700 dark:text-amber-300">
                  Você ainda pode continuar: o valor será confirmado no
                  servidor.
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={fetchUnitPrice}
                  disabled={purchasing || priceLoading}
                >
                  Tentar novamente
                </Button>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-border bg-background/50 p-3">
            <p className="text-xs text-foreground-muted mb-2">Quantidade</p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => changeQuantity(quantity - 1)}
                disabled={purchasing || quantity <= MIN_QUANTITY}
                aria-label="Diminuir quantidade"
              >
                <Minus className="w-4 h-4" />
              </Button>
              <div className="min-w-[72px] text-center text-lg font-semibold text-foreground-strong">
                {quantity}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => changeQuantity(quantity + 1)}
                disabled={purchasing || quantity >= MAX_QUANTITY}
                aria-label="Aumentar quantidade"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-background/50 p-3">
            <p className="text-xs text-foreground-muted">Total mensal</p>
            <p className="text-base font-semibold text-foreground-strong">
              {totalPrice ? `R$ ${totalPrice.toFixed(2)}` : "--"}
            </p>
          </div>

          {result && (
            <div
              className={`rounded-lg border p-3 text-sm ${
                result.success
                  ? "border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-300"
                  : "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"
              }`}
            >
              <div className="flex items-start gap-2">
                {result.success ? (
                  <CheckCircle2 className="w-4 h-4 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                )}
                <span>{result.message}</span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={purchasing}
          >
            Fechar
          </Button>
          <Button
            type="button"
            onClick={handlePurchase}
            className="bg-green-600 hover:bg-green-500 text-white"
            disabled={purchasing || priceLoading}
          >
            {purchasing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Comprando...
              </>
            ) : !unitPrice ? (
              "Comprar e confirmar valor"
            ) : (
              "Comprar e continuar ativação"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
